import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { from, of } from 'rxjs';
import { switchMap, map, catchError, take, finalize, concatMap, tap } from 'rxjs/operators';
import * as ExcelManagementActions from './excel-management.actions';
import { TExcelManagementActionType } from './excel-management.model';
import { ListingHelperClass } from '../../helpers/listing-helper';
import { UploadDownLoadFileService, AppNotificationUtilClass } from '@monorepo/services';
import { ExcelUtilClass } from '@monorepo/utils';
import { createFormData } from '../util';
import { OrderDetailsFacade } from '../order-details/order-details.facade';
import { ModalController } from '@ionic/angular';
import { Router } from '@angular/router';
import { EXCEL_CONTEXT, TExcelManagementContext } from '../../types';

const DOWNLOAD_FILE_NAME: Record<TExcelManagementContext, string> = {
  [EXCEL_CONTEXT.PRODUCED_QTY]: 'Planned-qty-order-details.xlsx',
  [EXCEL_CONTEXT.ORDERS_UPLOAD]: 'sample-orders-template.xlsx',
  [EXCEL_CONTEXT.MACHINE_AVAILABILITY]: 'machine-availability-details.xlsx',
  [EXCEL_CONTEXT.OPERATION_BREAKDOWN]: 'operation-breakdown-details.xlsx',
  [EXCEL_CONTEXT.MATERIAL_INHOUSE]: 'material-inhouse.xlsx',
};

const DOWNLOAD_ERROR_FILE_NAME: Record<TExcelManagementContext, string> = {
  [EXCEL_CONTEXT.PRODUCED_QTY]: 'produced-qty-error-template.xlsx',
  [EXCEL_CONTEXT.ORDERS_UPLOAD]: 'order-list-error-template.xlsx',
  [EXCEL_CONTEXT.MACHINE_AVAILABILITY]: 'machine-availability-error-template.xlsx',
  [EXCEL_CONTEXT.OPERATION_BREAKDOWN]: 'operation-breakdown-error-template.xlsx',
  [EXCEL_CONTEXT.MATERIAL_INHOUSE]: 'material-inhouse-error-template.xlsx',
};

@Injectable({ providedIn: 'root' })
export class ExcelManagementEffects {
  constructor(
    private actions$: Actions,
    private fileService: UploadDownLoadFileService,
    private APIHelper: AppNotificationUtilClass,
    private listingHelper: ListingHelperClass,
    private excelHelper: ExcelUtilClass,
    private orderDetailsFacade: OrderDetailsFacade,
    private modalController: ModalController,
    private router: Router,
  ) {}

  uploadExcel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TExcelManagementActionType.UploadExcel),

      switchMap((action: ExcelManagementActions.UploadExcel) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => [loaderEl, action] as const),
        ),
      ),

      map(([loaderEl, action]) => [loaderEl, action, createFormData(action.payload.file)] as const),

      switchMap(([loaderEl, action, uploadFormData]) =>
        this.fileService
          .uploadExcel(this.listingHelper.getFileUploadUrl(action.payload.excelInfo), uploadFormData)
          .pipe(
            finalize(() => this.APIHelper.dismissLoadingV2(loaderEl)),
            concatMap((res) => from(this.modalController.dismiss()).pipe(map(() => res))),
            concatMap((res) =>
              from(this.listingHelper.openUploadSummaryModal(res, action.payload.excelInfo)).pipe(
                concatMap((modal: HTMLIonModalElement) => from(modal.onDidDismiss())),
                tap(() => {
                  const info = action.payload.excelInfo;
                  switch (info.excelContext) {
                    case EXCEL_CONTEXT.PRODUCED_QTY:
                      this.orderDetailsFacade.fetchPoDetailscard({ id: info.orderId });
                      break;

                    case EXCEL_CONTEXT.ORDERS_UPLOAD:
                      this.router.navigate(['/orders/list'], { queryParams: { refresh: true } });
                      break;
                  }
                }),
                map(() => new ExcelManagementActions.UploadExcelSuccess()),
              ),
            ),
            catchError(() => {
              return of(new ExcelManagementActions.UploadExcelFailure());
            }),
          ),
      ),
    ),
  );

  downloadExcel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TExcelManagementActionType.DownloadExcel),
      switchMap((action: ExcelManagementActions.DownloadExcel) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => [loaderEl, action] as const),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.fileService
          .downloadSampleTemplatePlani8(
            this.listingHelper.getDownloadFileUrl(action.excelInfo),
            action.excelInfo.downloadPayload,
          )
          .pipe(
            finalize(() => this.APIHelper.dismissLoadingV2(loaderEl)),
            map((res) => {
              this.excelHelper.downloadFile(res, DOWNLOAD_FILE_NAME[action.excelInfo.excelContext]);
              return new ExcelManagementActions.DownloadExcelSuccess();
            }),
            catchError(() => of(new ExcelManagementActions.DownloadExcelFailure())),
          ),
      ),
    ),
  );

  downloadExcelErrorTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TExcelManagementActionType.DownloadErrorExcel),
      switchMap((action: ExcelManagementActions.DownloadErrorExcel) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => [loaderEl, action] as const),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.fileService
          .downloadErrorTemplatePlani8(
            this.listingHelper.getDownloadErrorTemplateUrl(action.payload.excelContext, action.payload.errorKey),
          )
          .pipe(
            finalize(() => this.APIHelper.dismissLoadingV2(loaderEl)),
            map((res) => {
              this.excelHelper.downloadFile(res, DOWNLOAD_ERROR_FILE_NAME[action.payload.excelContext]);
              return new ExcelManagementActions.DownloadExcelSuccess();
            }),
            catchError(() => of(new ExcelManagementActions.DownloadErrorExcelFailure())),
          ),
      ),
    ),
  );
}
